import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, ShieldAlert, PauseCircle, PlayCircle, RefreshCw,
  Activity, DollarSign, AlertTriangle, CheckCircle2, XCircle, Zap,
  FlaskConical,
} from "lucide-react";
import { toast } from "sonner";

const BASE = "/coach-llm/admin";
const fmtUsd = (n) => (n == null ? "—" :
  `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`);

export default function CoachAdminPanel() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [health, setHealth] = useState([]);
  const [audit, setAudit] = useState([]);
  const [harness, setHarness] = useState(null);
  const [harnessRun, setHarnessRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pauseReason, setPauseReason] = useState("");
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o, e, h, a, hst] = await Promise.all([
        api.get(`${BASE}/status`),
        api.get(`${BASE}/usage/overview`),
        api.get(`${BASE}/usage/events?limit=25`),
        api.get(`${BASE}/health-events?limit=25`),
        api.get(`${BASE}/audit?limit=25`),
        api.get(`${BASE}/harness/status`),
      ]);
      setStatus(s.data); setOverview(o.data); setEvents(e.data);
      setHealth(h.data); setAudit(a.data); setHarness(hst.data);
    } catch (err) {
      toast.error("Load failed: " + (err?.response?.data?.detail || err.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePlatform = async (pause) => {
    if (!pauseReason || pauseReason.length < 3) {
      toast.error("Reason required (min 3 chars) for audit trail.");
      return;
    }
    if (!window.confirm(`${pause ? "PAUSE" : "RESUME"} all Coach AI features platform-wide?`)) return;
    try {
      await api.post(`${BASE}/platform`,
        { paused: pause, reason: pauseReason, confirm: true });
      toast.success(pause ? "Platform paused" : "Platform resumed");
      setPauseReason("");
      load();
    } catch (err) {
      toast.error("Toggle failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const runPreflight = async () => {
    try {
      const { data } = await api.post(`${BASE}/preflight`);
      if (data.ok === false) toast.error(`Preflight failed: ${data.reason || "unknown"}`);
      else toast.success("Preflight: models look healthy");
    } catch (err) {
      toast.error("Preflight failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const runHarness = async () => {
    if (!harness?.enabled) {
      toast.error("Harness disabled. Set COACH_LLM_HARNESS_ENABLED=true in Preview backend.");
      return;
    }
    if (!window.confirm("Run side-by-side harness now? This will make provider calls.")) return;
    setRunning(true);
    try {
      const { data } = await api.post(`${BASE}/harness/run`);
      setHarnessRun(data);
      toast.success(`Harness run complete — ${data.cases.length} cases`);
    } catch (err) {
      toast.error("Harness failed: " + (err?.response?.data?.detail || err.message));
    } finally { setRunning(false); }
  };

  const totalCost = overview?.month?.cost ?? 0;
  const budget = overview?.budget?.monthly_budget_usd;
  const budgetPct = overview?.budget?.current_percent;
  const budgetWarn = overview?.budget?.warning;
  const topTenant = useMemo(() => (overview?.by_tenant || [])[0] || null, [overview]);

  if (loading) return (
    <div className="p-8 text-sm text-zinc-500" data-testid="coach-admin-loading">Loading…</div>
  );

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="coach-admin-panel">
      <button onClick={() => nav("/app/coach")} data-testid="coach-admin-back"
              className="flex items-center gap-1 text-sm text-zinc-500 hover:text-[#FF5A00] mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to Coach
      </button>
      <div className="flex items-baseline justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-[#FF5A00] mb-1">
            Super-Admin · Coach LLM
          </div>
          <h1 className="font-heading text-3xl font-bold">Coach AI platform controls</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Estimated cost + usage. Values are estimates from the versioned pricing
            table (<span className="font-mono">{overview?.pricing_version}</span>)
            until reconciled with OpenAI billing.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="coach-admin-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* KILL SWITCH BANNER */}
      {status?.env_kill_switch && (
        <div className="mb-4 p-4 rounded-lg bg-red-950/40 border border-red-900 flex items-center gap-3"
             data-testid="killswitch-banner">
          <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
          <div>
            <div className="font-semibold text-red-200">
              Environment hard kill-switch is ACTIVE
            </div>
            <div className="text-xs text-red-300/70">
              COACH_LLM_KILL_SWITCH=true. UI cannot re-enable — must be cleared in the backend env.
            </div>
          </div>
        </div>
      )}

      {/* COST WIDGET */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-4" data-testid="cost-today">
          <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Today
          </div>
          <div className="text-2xl font-heading">{fmtUsd(overview?.today?.cost)}</div>
          <div className="text-xs text-zinc-500 mt-1">{overview?.today?.calls || 0} calls</div>
        </Card>
        <Card className="p-4" data-testid="cost-month">
          <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> This month
          </div>
          <div className="text-2xl font-heading">{fmtUsd(totalCost)}</div>
          <div className="text-xs text-zinc-500 mt-1">
            {overview?.month?.calls || 0} calls
          </div>
        </Card>
        <Card className={"p-4 " + (budgetWarn ? "border-amber-600/60" : "")}
              data-testid="cost-budget">
          <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
            <Activity className="w-3 h-3" /> Budget
          </div>
          <div className="text-2xl font-heading">
            {budget ? `${budgetPct?.toFixed(1)}%` : "—"}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {budget ? `of ${fmtUsd(budget)}/mo` : "No budget set"}
            {budgetWarn && (
              <span className="ml-2 text-amber-500">
                <AlertTriangle className="w-3 h-3 inline" /> WARN
              </span>
            )}
          </div>
        </Card>
        <Card className="p-4" data-testid="cost-failures">
          <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
            <XCircle className="w-3 h-3" /> Failed / Fallback
          </div>
          <div className="text-2xl font-heading">
            {overview?.totals?.failed || 0} · {overview?.totals?.fallback || 0}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {topTenant ? `Top: ${topTenant.business_id?.slice(0,6)}… ${fmtUsd(topTenant.cost)}` : "No tenant activity"}
          </div>
        </Card>
      </div>

      {/* PROVIDER STATUS + PLATFORM PAUSE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="p-5" data-testid="provider-status">
          <div className="font-semibold mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#FF5A00]" /> Provider status
          </div>
          <div className="text-sm space-y-1.5">
            <Row label="Provider" value={status?.provider} />
            <Row label="OpenAI key" value={status?.openai_key_configured ? "configured" : "MISSING"}
                  ok={status?.openai_key_configured} />
            <Row label="Emergent key" value={status?.emergent_key_configured ? "configured" : "not set (optional)"} />
            <Row label="Structured outputs" value={status?.structured_outputs || "strict"} />
            <Row label="Env kill switch" value={status?.env_kill_switch ? "ACTIVE" : "off"}
                  ok={!status?.env_kill_switch} />
            <Row label="Platform paused" value={status?.platform_paused ? "PAUSED" : "running"}
                  ok={!status?.platform_paused} />
          </div>
          <div className="mt-3 text-xs text-zinc-500 space-y-1">
            {Object.entries(status?.models || {}).map(([feat, m]) => (
              <div key={feat}><span className="font-mono">{feat}:</span> {m.current}</div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="mt-3"
                   onClick={runPreflight} data-testid="preflight-btn">
            Run preflight
          </Button>
        </Card>

        <Card className="p-5" data-testid="platform-controls">
          <div className="font-semibold mb-3 flex items-center gap-2">
            <PauseCircle className="w-4 h-4 text-amber-500" /> Emergency pause
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            Pausing halts Coach AI for every tenant immediately. Requires an audit reason.
          </p>
          <Textarea rows={2} placeholder="Reason (min 3 chars) — recorded in audit log"
                     value={pauseReason} onChange={(e) => setPauseReason(e.target.value)}
                     data-testid="pause-reason" />
          <div className="flex gap-2 mt-3">
            <Button variant="destructive" size="sm" onClick={() => togglePlatform(true)}
                     disabled={status?.platform_paused || status?.env_kill_switch}
                     data-testid="pause-platform">
              <PauseCircle className="w-3 h-3 mr-1" /> Pause AI
            </Button>
            <Button variant="outline" size="sm" onClick={() => togglePlatform(false)}
                     disabled={!status?.platform_paused || status?.env_kill_switch}
                     data-testid="resume-platform">
              <PlayCircle className="w-3 h-3 mr-1" /> Resume AI
            </Button>
          </div>
        </Card>
      </div>

      {/* BY FEATURE / MODEL / PROVIDER */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <BucketCard title="By feature" rows={overview?.by_feature} field="feature"
                     testid="by-feature" />
        <BucketCard title="By model" rows={overview?.by_model} field="actual_model"
                     testid="by-model" />
        <BucketCard title="By tenant (top 10)"
                     rows={(overview?.by_tenant || []).slice(0, 10)} field="business_id"
                     testid="by-tenant" />
      </div>

      {/* HARNESS */}
      <Card className="p-5 mb-6" data-testid="harness-card">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-purple-500" /> Side-by-side harness
            <Badge variant={harness?.enabled ? "default" : "outline"} className="ml-1">
              {harness?.enabled ? "enabled" : "disabled"}
            </Badge>
          </div>
          <Button size="sm" onClick={runHarness}
                   disabled={running || !harness?.enabled}
                   data-testid="run-harness">
            {running ? "Running…" : "Run harness"}
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mb-2">
          Preview only. Requires <span className="font-mono">COACH_LLM_HARNESS_ENABLED=true</span>.
          Runs a sanitized micro-corpus through direct_openai + emergent + deterministic
          and compares schema validity, latency, cost, and expected labels.
        </p>
        {harnessRun && (
          <div className="mt-3 space-y-3">
            {harnessRun.cases.map((c) => (
              <div key={c.id} className="border rounded-md p-3 border-zinc-800"
                    data-testid={`harness-case-${c.id}`}>
                <div className="text-sm font-semibold flex items-center justify-between">
                  <span>{c.id}</span>
                  <span className="text-xs text-zinc-500">
                    input_sha256={c.input_sha256} · len={c.input_len}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                  {c.results.map((r, i) => (
                    <div key={i} className="border rounded p-2 border-zinc-800/50">
                      <div className="font-mono text-[#FF5A00]">{r.provider_requested}</div>
                      <div>ok: {r.ok ? <CheckCircle2 className="w-3 h-3 inline text-green-500" />
                                   : <XCircle className="w-3 h-3 inline text-red-500" />}</div>
                      <div>model: {r.model || "—"}</div>
                      <div>latency: {r.latency_ms}ms</div>
                      <div>tokens: {r.input_tokens}/{r.output_tokens}</div>
                      <div>cost: {fmtUsd(r.estimated_cost_usd)}</div>
                      <div>error: {r.error_code || "—"}</div>
                      {r.eval?.expected_intent_hit != null && (
                        <div>intent✓: {String(r.eval.expected_intent_hit)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* HEALTH + AUDIT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <Card className="p-4" data-testid="health-events">
          <div className="font-semibold mb-2 text-sm">Health events</div>
          {(health.length ? health : [{feature: "—", from_model: "—", to_model: "—", reason: "none yet", at: ""}]).map((e, i) => (
            <div key={i} className="text-xs py-1 border-b border-zinc-800/50 last:border-0">
              <span className="font-mono">{e.feature}</span> · {e.from_model} → {e.to_model}
              <span className="text-zinc-500 ml-1">({e.reason})</span>
              {e.at && <span className="text-zinc-500 float-right">{e.at.slice(11, 19)}</span>}
            </div>
          ))}
        </Card>
        <Card className="p-4" data-testid="audit-events">
          <div className="font-semibold mb-2 text-sm">Audit</div>
          {(audit.length ? audit : [{event: "—", actor: "", reason: "no history yet", at: ""}]).map((e, i) => (
            <div key={i} className="text-xs py-1 border-b border-zinc-800/50 last:border-0">
              <span className="font-mono">{e.event}</span>
              <span className="text-zinc-500"> by {e.actor || "—"}</span>
              {e.reason && <span className="text-zinc-500"> — {e.reason.slice(0, 60)}</span>}
              {e.at && <span className="text-zinc-500 float-right">{e.at.slice(11, 19)}</span>}
            </div>
          ))}
        </Card>
      </div>

      {/* RECENT USAGE EVENTS */}
      <Card className="p-5" data-testid="recent-usage">
        <div className="font-semibold mb-3 text-sm">Recent usage events (last 25)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-zinc-500">
              <tr className="text-left">
                <th className="py-1">Time</th><th>Tenant</th><th>Feature</th>
                <th>Provider</th><th>Model</th><th>Tokens</th>
                <th>Cost</th><th>Latency</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i} className="border-t border-zinc-800/50">
                  <td className="py-1 font-mono">{(e.created_at || "").slice(11, 19)}</td>
                  <td className="font-mono">{(e.business_id || "").slice(0, 6)}…</td>
                  <td>{e.feature}</td>
                  <td className="font-mono">{e.provider}</td>
                  <td className="font-mono">{e.actual_model || "—"}</td>
                  <td>{e.input_tokens}/{e.output_tokens}</td>
                  <td>{fmtUsd(e.estimated_cost_usd)}</td>
                  <td>{e.latency_ms}ms</td>
                  <td>{e.success
                    ? <CheckCircle2 className="w-3 h-3 inline text-green-500" />
                    : <span className="text-red-400">{e.failure_category}</span>}
                    {e.fallback_used && <span className="ml-1 text-amber-400">(fb)</span>}
                  </td>
                </tr>
              ))}
              {!events.length && (
                <tr><td colSpan={9} className="py-4 text-center text-zinc-500">
                  No calls recorded yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className={ok === false ? "text-red-400" : (ok === true ? "text-green-500" : "")}>
        {String(value ?? "—")}
      </span>
    </div>
  );
}

function BucketCard({ title, rows, field, testid }) {
  const total = (rows || []).reduce((a, r) => a + (r.cost || 0), 0);
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="font-semibold mb-2 text-sm">{title}</div>
      {!rows?.length && <div className="text-xs text-zinc-500">No data yet</div>}
      {(rows || []).map((r, i) => (
        <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-800/40 last:border-0">
          <span className="font-mono truncate max-w-[180px]" title={r[field]}>
            {r[field] || "—"}
          </span>
          <span>{fmtUsd(r.cost)} · {r.calls}</span>
        </div>
      ))}
      {rows?.length ? (
        <div className="flex justify-between text-xs pt-2 mt-1 border-t border-zinc-800 font-semibold">
          <span>Total</span><span>{fmtUsd(total)}</span>
        </div>
      ) : null}
    </Card>
  );
}
